/**
 * Pure helpers behind the ONE grouped field picker that Filter, Group and
 * Sort all render (Signals V3 PR 1, U2). Glaze-free and React-free by design,
 * like the rest of `table-core/`.
 *
 * The three pickers used to read three different lists (the filter catalog,
 * `config.columns` alphabetised, and `config.sortOptions`), so the same field
 * had a different name, position and heading in each. Everything here exists
 * to make them agree: one ordering (the filter catalog's), one set of group
 * headings, one search rule.
 */

import { fieldExamples } from '@shared/flow/field-catalog-order';

export interface FieldSection<T> {
  /** `null` = a headingless run (options that carry no `group`). */
  group: string | null;
  entries: T[];
}

/**
 * Folds consecutive same-group options into sections without re-sorting, so
 * the caller's order (and therefore heading order) is preserved exactly. A
 * catalog that is already group-contiguous (`groupContiguous`,
 * `@shared/flow/field-catalog-order`) yields each heading once.
 */
export function sectionFieldOptions<T extends { group?: string }>(options: readonly T[]): FieldSection<T>[] {
  const sections: FieldSection<T>[] = [];
  for (const option of options) {
    const group = option.group ?? null;
    const last = sections[sections.length - 1];
    if (last && last.group === group) last.entries.push(option);
    else sections.push({ group, entries: [option] });
  }
  return sections;
}

/**
 * Search rule shared by every field picker: label or group (section name), never the example line —
 * typing "flow" must not find Signal ID through its example. Case-insensitive; blank matches all.
 */
export function matchesFieldQuery(option: { label: string; group?: string }, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return option.label.toLowerCase().includes(q) || (option.group?.toLowerCase().includes(q) ?? false);
}

/** The slice of a filter-catalog entry `catalogOrdered` reads. */
export interface CatalogOrderEntry {
  key: string;
  group: string;
  values?: ReadonlyArray<string | { value: string; label: string }>;
  hint?: string;
}

export interface CatalogOrderOptions {
  /** Heading for keys the filter catalog has no entry for (tracker, group-only keys). */
  trailingGroup: string;
}

/**
 * Orders `items` by the filter catalog's own order and stamps each with its
 * catalog `group` and example line (`hint`, via `fieldExamples` — only when the
 * entry has one). Keys the catalog doesn't carry trail in their incoming
 * order under `trailingGroup`.
 */
export function catalogOrdered<T extends { key: string }>(
  items: readonly T[],
  catalog: readonly CatalogOrderEntry[],
  { trailingGroup }: CatalogOrderOptions,
): Array<T & { group: string; hint?: string }> {
  const index = new Map(catalog.map((entry, i) => [entry.key, i]));
  const byKey = new Map(catalog.map(entry => [entry.key, entry]));
  const inCatalog: Array<{ item: T & { group: string; hint?: string }; at: number }> = [];
  const trailing: Array<T & { group: string; hint?: string }> = [];
  for (const item of items) {
    const entry = byKey.get(item.key);
    if (!entry) {
      trailing.push({ ...item, group: trailingGroup });
      continue;
    }
    const hint = fieldExamples(entry);
    inCatalog.push({ item: { ...item, group: entry.group, ...(hint !== undefined ? { hint } : {}) }, at: index.get(item.key)! });
  }
  inCatalog.sort((a, b) => a.at - b.at);
  return [...inCatalog.map(c => c.item), ...trailing];
}
